// deer.js — wild deer roaming the distant field, in front of the forest ring.
//
// A small herd of animated GLB deer (Deer.glb + the antlered Stag.glb) that graze,
// wander the meadow band just inside the forest/mountain rings, occasionally bolt
// into the treeline and vanish for a while, then trot back out — so the far field
// feels alive rather than empty. They're tied to the scenery, not a toggle of their
// own: environment.js calls setDeerActive(true) whenever the mountain OR forest ring
// is showing (see refreshBackdropGround), and hides them when both are off.
//
// Built lazily on first activation: each GLB is fetched once, then SkeletonUtils
// clones give every deer its own skeleton + AnimationMixer so they animate
// independently. Per-frame work is updateDeer(dt), called from main.js's animate().
//
// All randomness here is cosmetic (Math.random / gsap-free), never the seeded tree
// stream — deer wandering must not perturb reproducible tree geometry.

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { app } from './core.js';

const loader = new GLTFLoader();

// The eight animation variants we drive (the only clips requested). Loaded by these
// exact names from the GLB; both models ship the same set.
const CLIPS = ['Eating', 'Gallop', 'Gallop_Jump', 'Jump_toIdle', 'Walk', 'Idle_Headlow', 'Idle_2', 'Idle'];
// Grazing picks one of these stationary clips, weighted heavily toward eating (the
// deer are mostly here to feed), with the idles as occasional variety.
const GRAZE_CLIPS = ['Eating', 'Eating', 'Eating', 'Eating', 'Eating', 'Idle_Headlow', 'Idle_Headlow', 'Idle', 'Idle_2'];

// Field band the herd roams: open meadow well in front of the forest (radius 1760).
// Kept a good way in from the treeline because the scene's exponential fog
// (FogExp2 ~0.00078) washes anything past ~1300 down to a faint smudge — out here
// the deer still read as colour, then fade into the haze when they bolt for the trees.
const FIELD_R_MIN = 1460;
const FIELD_R_MAX = 1740;
// Where a fleeing deer disappears into / emerges from — at/just past the treeline.
const FOREST_R_MIN = 1770;
const FOREST_R_MAX = 1860;
const GROUND_Y = -30; // distant ground disc height; feet rest here

const WALK_SPEED = 52;     // world units/sec — an amble across the meadow
const GALLOP_SPEED = 235;  // a hard run for the treeline
const WALK_TURN = 2.2;     // rad/sec yaw rate while walking
const GALLOP_TURN = 1.5;   // wider turns at a gallop
const ARRIVE = 28;         // distance to a target that counts as "arrived"

// Mid-walk hop (Jump_toIdle): occasionally a deer leaps as if clearing a small obstacle,
// keeping its forward momentum through the jump and then resuming the same walk.
const WALK_HOP_CHANCE = 0.35;   // odds a given walk includes one hop
const HOP_DUR = 0.7;            // seconds the hop clip runs before resuming the walk
const HOP_SPEED_MULT = 1.7;     // forward speed boost during the leap (vs WALK_SPEED)

// Herd cohesion: the whole herd grazes around one shared "centre" on the ring that
// drifts slowly, so the deer cluster and migrate together instead of scattering.
// Each deer's wander/spawn target is a small offset off that centre.
const HERD_ANGLE_SPREAD = 0.085;  // ± radians around the centre angle (cluster width)
const HERD_RADIUS_SPREAD = 80;    // ± world units around the centre radius (cluster depth)
const HERD_DRIFT = 0.012;         // rad/sec the grazing ground slides around the ring
const STAG_SPOOK_CHANCE = 0.12;   // per graze cycle, odds the stag bolts (and the herd with it)

// The GLB deer face +Z in their own space, so heading = atan2(dir.x, dir.z) aims them
// down their travel direction. If they ever run tail-first, flip this to Math.PI.
const FACING_YAW = 0;

let deerGroup = null;   // parent of every deer; .visible mirrors _active
let herd = [];          // per-deer state objects
let _active = false;    // a ring is showing → herd is live
let _building = false;  // guard against double-loading on rapid toggles

// The herd's shared grazing ground: an angle + radius on the ring. Drifts each frame
// (updateDeer) so the cluster migrates; jumps to a new angle when the herd is spooked,
// so they regroup somewhere fresh after bolting into the trees.
const herdCenter = { ang: Math.random() * Math.PI * 2, r: (FIELD_R_MIN + FIELD_R_MAX) / 2 };

// Cache each GLB's raw bytes once; every deer is then GLTFLoader.parse()'d fresh from
// them so it gets its own independently-bound skeleton. (Cloning the loaded scene with
// SkeletonUtils left these particular rigs unbound — the skinned meshes collapsed and
// never drew — so we re-parse per instance instead.)
const buffers = {}; // { 'assets/models/Deer.glb': ArrayBuffer, ... }

function rand(a, b) { return a + Math.random() * (b - a); }

// A point in the herd's current cluster: a small offset off the shared herd centre.
// Used for both spawning and wandering, so the deer always congregate around the same
// drifting spot rather than spreading around the whole ring. Because every target sits
// near one angle, walks stay short local moves that never cross the centre (the zen
// garden); the radius clamp in moveToward is the hard backstop.
function herdPoint(spread = HERD_ANGLE_SPREAD) {
  const ang = herdCenter.ang + rand(-spread, spread);
  const r = Math.min(Math.max(herdCenter.r + rand(-HERD_RADIUS_SPREAD, HERD_RADIUS_SPREAD), FIELD_R_MIN), FIELD_R_MAX);
  return { x: Math.cos(ang) * r, z: Math.sin(ang) * r };
}

// A point at the treeline near a given angle (so a deer bolts roughly outward into
// the forest rather than sideways across it).
function forestPoint(nearAngle) {
  const ang = nearAngle + rand(-0.25, 0.25);
  const r = rand(FOREST_R_MIN, FOREST_R_MAX);
  return { x: Math.cos(ang) * r, z: Math.sin(ang) * r };
}

// Turn `cur` toward `goal` by at most `step` radians, on the shortest arc.
function turnToward(cur, goal, step) {
  let d = goal - cur;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  if (Math.abs(d) <= step) return goal;
  return cur + Math.sign(d) * step;
}

// Crossfade a deer to one of its animation clips.
function play(d, name, fade = 0.35) {
  const next = d.actions[name];
  if (!next || d.current === next) return;
  next.reset();
  next.enabled = true;
  next.setEffectiveWeight(1);
  next.play();
  if (d.current) next.crossFadeFrom(d.current, fade, false);
  d.current = next;
  d.currentName = name;
}

// ── State machine ─────────────────────────────────────────────────────────────
// graze: stand and feed/idle. walk: amble to a cluster point. hop: a brief mid-walk
// Jump_toIdle leap (keeps moving) as if clearing a small obstacle, then back to walk.
// run: gallop to the treeline. gone: hidden offstage for a beat. spooked: startled by
// the stag, a short pause before breaking into a run with the rest of the herd.

function enterGraze(d) {
  d.state = 'graze';
  d.timer = rand(4, 9);
  play(d, GRAZE_CLIPS[(Math.random() * GRAZE_CLIPS.length) | 0]);
}

function enterWalk(d) {
  d.state = 'walk';
  d.target = herdPoint();
  // Maybe schedule a single hop partway through this leg, as if clearing a small
  // obstacle. -1 means no hop this walk. walkTime tracks elapsed time toward it.
  d.hopAt = Math.random() < WALK_HOP_CHANCE ? rand(0.6, 2.2) : -1;
  d.walkTime = 0;
  play(d, 'Walk');
}

// Resume walking toward the SAME target after a hop (no new target, no second hop),
// so the leap reads as a beat within one continuous walk.
function resumeWalk(d) {
  d.state = 'walk';
  d.hopAt = -1;
  d.walkTime = 0;
  play(d, 'Walk', 0.2);
}

// A leap mid-walk: play Jump_toIdle but keep driving forward (see the 'hop' tick), then
// pick the walk back up — so it looks like hopping over something, not stopping.
function enterHop(d) {
  d.state = 'hop';
  d.timer = HOP_DUR;
  play(d, 'Jump_toIdle', 0.12);
}

function enterRun(d) {
  d.state = 'run';
  const ang = Math.atan2(d.root.position.z, d.root.position.x);
  d.target = forestPoint(ang);
  d.jumpTimer = rand(1.2, 2.6); // periodic Gallop_Jump leaps while running
  play(d, 'Gallop');
}

function enterGone(d) {
  d.state = 'gone';
  d.timer = rand(7, 20);
  d.root.visible = false;
}

// The stag bolts and the whole herd goes with it: the leader breaks for the treeline
// immediately, the does a beat later (so it reads as them reacting to him, not a
// synchronized teleport). The grazing ground jumps to a new angle so when they filter
// back out of the trees they regroup somewhere fresh. Deer already offstage are left be.
function spookHerd(initiator) {
  herdCenter.ang = Math.random() * Math.PI * 2;
  for (const d of herd) {
    if (d.state === 'gone') continue;
    if (d === initiator) {
      enterRun(d);
    } else {
      d.state = 'spooked';
      d.timer = rand(0.15, 0.7); // brief startle before following the stag
    }
  }
}

// Re-emerge at the back of the meadow against the treeline, near the herd centre, then
// amble back in to regroup. A slightly wider angular spread than the grazing cluster so
// they trot out of the trees in a loose line rather than stacked. Sits just inside
// FIELD_R_MAX so the ring-clamp doesn't snap it.
function respawn(d) {
  const ang = herdCenter.ang + rand(-HERD_ANGLE_SPREAD * 2, HERD_ANGLE_SPREAD * 2);
  const r = rand(FIELD_R_MAX - 60, FIELD_R_MAX);
  d.root.position.set(Math.cos(ang) * r, GROUND_Y, Math.sin(ang) * r);
  d.root.visible = true;
  enterWalk(d);
}

// Move a deer toward d.target at `speed`, steering its yaw to face travel. Returns
// true once it arrives. When `clampRing` is set (roaming the meadow) the deer's
// distance from the garden is pinned to [FIELD_R_MIN, FIELD_R_MAX] every step, so it
// physically cannot drift into the zen garden or past the band — a hard backstop on
// top of the short-arc wander targets. Fleeing deer pass clampRing=false so they can
// run out through the band to the treeline.
function moveToward(d, speed, turnRate, dt, clampRing) {
  const p = d.root.position;
  const dx = d.target.x - p.x;
  const dz = d.target.z - p.z;
  const dist = Math.hypot(dx, dz);
  if (dist < ARRIVE) return true;

  const goalYaw = Math.atan2(dx, dz) + FACING_YAW;
  d.root.rotation.y = turnToward(d.root.rotation.y, goalYaw, turnRate * dt);

  const stepLen = Math.min(speed * dt, dist);
  p.x += (dx / dist) * stepLen;
  p.z += (dz / dist) * stepLen;

  if (clampRing) {
    const rr = Math.hypot(p.x, p.z);
    if (rr < FIELD_R_MIN || rr > FIELD_R_MAX) {
      const s = Math.min(Math.max(rr, FIELD_R_MIN), FIELD_R_MAX) / rr;
      p.x *= s; p.z *= s;
    }
  }
  return false;
}

function tick(d, dt) {
  d.mixer.update(dt);

  switch (d.state) {
    case 'graze':
      d.timer -= dt;
      if (d.timer <= 0) {
        // Only the stag starts a flight; the does just feed and amble (they flee only
        // when he does, via spookHerd). Keeps the herd calm and stag-led.
        if (d.kind === 'stag' && Math.random() < STAG_SPOOK_CHANCE) spookHerd(d);
        else if (Math.random() < 0.35) enterGraze(d); // settle in and keep feeding
        else enterWalk(d);                            // wander on, toward the cluster
      }
      break;

    case 'walk':
      d.walkTime += dt;
      if (d.hopAt > 0 && d.walkTime >= d.hopAt) { enterHop(d); break; } // leap mid-stride
      if (moveToward(d, WALK_SPEED, WALK_TURN, dt, true)) enterGraze(d);
      break;

    case 'hop':
      // Keep the forward momentum (a touch faster, the leap) through the jump, steering
      // only gently, then resume the walk — or graze if we happened to reach the target.
      d.timer -= dt;
      if (moveToward(d, WALK_SPEED * HOP_SPEED_MULT, WALK_TURN * 0.5, dt, true)) enterGraze(d);
      else if (d.timer <= 0) resumeWalk(d);
      break;

    case 'spooked':
      // Startled by the stag; hold a beat, then break for the treeline too.
      d.timer -= dt;
      if (d.timer <= 0) enterRun(d);
      break;

    case 'run':
      d.jumpTimer -= dt;
      if (d.jumpTimer <= 0) {
        // A leaping bound, then back to the flat gallop.
        play(d, d.currentName === 'Gallop' ? 'Gallop_Jump' : 'Gallop', 0.2);
        d.jumpTimer = rand(1.0, 2.4);
      }
      if (moveToward(d, GALLOP_SPEED, GALLOP_TURN, dt)) enterGone(d);
      break;

    case 'gone':
      d.timer -= dt;
      if (d.timer <= 0) respawn(d);
      break;
  }
}

// Build one deer from a cached source GLTF, scaled to `height` and seeded into a
// staggered starting state so the herd doesn't move in lockstep. `kind` is 'stag' for
// the lone male (the one that can spook the herd) or 'doe' for the rest.
function spawnDeer(src, height, kind) {
  const model = src.scene; // a freshly-parsed scene per deer → its own bound skeleton

  const size = new THREE.Vector3();
  new THREE.Box3().setFromObject(model).getSize(size);
  model.scale.setScalar(height / size.y);

  // Re-measure to rest the feet on the ground disc.
  const box = new THREE.Box3().setFromObject(model);
  const root = new THREE.Group();
  model.position.y = -box.min.y;
  root.add(model);

  // Far-off accents: skip shadows (well outside the shadow frustum) but keep them
  // out of the prune/shape raycast so a stray click never grabs a deer.
  model.traverse((o) => {
    if (o.isMesh) {
      o.castShadow = false; o.receiveShadow = false; o.raycast = () => {};
      // Animated SkinnedMeshes whose rest-pose bounding sphere doesn't cover the
      // clip's motion get wrongly frustum-culled — disable culling so they always draw.
      o.frustumCulled = false;
    }
  });

  const mixer = new THREE.AnimationMixer(model);
  const actions = {};
  for (const name of CLIPS) {
    const clip = THREE.AnimationClip.findByName(src.animations, name);
    if (clip) actions[name] = mixer.clipAction(clip);
  }

  const d = { root, mixer, actions, kind, current: null, currentName: null, state: 'graze', timer: 0, target: null, jumpTimer: 0 };

  // Start each deer in the herd cluster (a touch wider than the graze spread) and
  // desync their clocks so they don't move in lockstep.
  const p = herdPoint(HERD_ANGLE_SPREAD * 1.6);
  root.position.set(p.x, GROUND_Y, p.z);
  root.rotation.y = Math.random() * Math.PI * 2;

  // Kick off in a mix of states so some graze, some are already crossing the field.
  const r = Math.random();
  if (r < 0.6) enterGraze(d);
  else enterWalk(d);
  d.timer *= Math.random(); // partial timers → staggered first transition

  deerGroup.add(root);
  herd.push(d);
}

const DEER_FILE = 'assets/models/Deer.glb';
const STAG_FILE = 'assets/models/Stag.glb';
const DEER_HEIGHT = 36;
const STAG_HEIGHT = 56;

// Parse one fresh deer from the cached bytes and add it to the herd.
function parseDeer(file, height, kind) {
  loader.parse(buffers[file], '', (gltf) => {
    spawnDeer({ scene: gltf.scene, animations: gltf.animations }, height, kind);
  }, (err) => console.error('[deer] parse failed', file, err));
}

// Fetch each GLB's bytes once, then populate the herd by re-parsing per individual:
// a handful of does and a couple of antlered stags, spread across the meadow.
function buildHerd() {
  _building = true;
  Promise.all([DEER_FILE, STAG_FILE].map(
    (file) => fetch(file).then((r) => r.arrayBuffer()).then((buf) => { buffers[file] = buf; })
  )).then(() => {
    _building = false;
    // Background dressing: a small herd of four does and a lone stag (five total).
    for (let i = 0; i < 4; i++) parseDeer(DEER_FILE, DEER_HEIGHT, 'doe');
    parseDeer(STAG_FILE, STAG_HEIGHT, 'stag');
  }).catch((err) => { _building = false; console.error('[deer] failed to fetch GLBs', err); });
}

// Show/hide the herd in step with the scenery rings. First activation lazily builds
// it; later toggles are a cheap group-visibility flip.
export function setDeerActive(on) {
  _active = !!on;
  if (on && !deerGroup) {
    deerGroup = new THREE.Group();
    app.scene.add(deerGroup);
  }
  if (on && !herd.length && !_building) buildHerd();
  if (deerGroup) deerGroup.visible = _active;
}

// Per-frame: drift the shared grazing ground, then advance every deer's mixer + AI.
// Cheap no-op when the herd is hidden.
export function updateDeer(dt) {
  if (!_active || !herd.length) return;
  const step = Math.min(dt, 0.05); // clamp tab-switch hitches so deer don't teleport
  herdCenter.ang += HERD_DRIFT * step; // the cluster slowly migrates around the ring
  for (const d of herd) tick(d, step);
}

// ── Coupling for the Shiba pup (shiba.js) ─────────────────────────────────────
// The deer stay out in their meadow ring — the pup runs to THEM. nearestDeer
// returns the closest calm (grazing/ambling) deer to the given point as
// {x, z, dist}, or null. Deer already fleeing (run/spooked/gone) are excluded, so
// once the pup spooks the herd this returns null and it heads home.
export function nearestDeer(x, z) {
  if (!_active) return null;
  let best = null, bestD = Infinity;
  for (const d of herd) {
    if (!d.root.visible) continue;
    if (d.state !== 'graze' && d.state !== 'walk' && d.state !== 'hop') continue;
    const p = d.root.position;
    const dd = Math.hypot(p.x - x, p.z - z);
    if (dd < bestD) { bestD = dd; best = { x: p.x, z: p.z, dist: dd }; }
  }
  return best;
}

// Scare the whole herd into bolting for the treeline (and then hiding) — the pup
// calls this once it's charged close enough.
export function spookDeerHerd() {
  if (!herd.length) return;
  const initiator = herd.find((d) => d.state !== 'gone');
  if (initiator) spookHerd(initiator);
}
